import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { ImageDrop, Thumb } from "./ImageUpload";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="kicker">{label}</span>
    {hint && <span className="block text-xs text-ink/60 mt-1">{hint}</span>}
    <div className="mt-2">{children}</div>
  </label>
);

const SIZES = [
  ["small", "Small"],
  ["medium", "Medium"],
  ["large", "Large"],
];
const PREVIEW_SIZE = { small: "text-xl sm:text-2xl", medium: "text-3xl sm:text-4xl", large: "text-5xl sm:text-6xl" };

const SizePicker = ({ value, onChange }) => (
  <div>
    <p className="kicker">Title size</p>
    <div className="grid grid-cols-3 mt-2 border-2 border-ink" role="radiogroup" data-testid="banner-size-picker">
      {SIZES.map(([v, label], i) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={`py-3 flex items-end justify-center gap-2 transition-colors ${i ? "border-l-2 border-ink" : ""} ${value === v ? "bg-ink text-cream" : "hover:bg-blush"}`}
          data-testid={`banner-size-${v}`}
        >
          <span className={`display leading-none ${{ small: "text-lg", medium: "text-2xl", large: "text-3xl" }[v]}`}>Aa</span>
          <span className="kicker">{label}</span>
        </button>
      ))}
    </div>
  </div>
);

const MiniPreview = ({ f }) => (
  <div className="relative aspect-[16/10] overflow-hidden bg-ink text-cream border-2 border-ink" data-testid="banner-mini-preview">
    {f.image && <img src={f.image} alt="" className="absolute inset-0 h-full w-full object-cover" />}
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
    <div className="absolute left-5 right-5 bottom-5">
      {f.kicker && <span className="kicker bg-hot text-ink px-2 py-1 text-[10px]">{f.kicker}</span>}
      <p className={`display ${PREVIEW_SIZE[f.title_size] ?? PREVIEW_SIZE.medium} leading-[0.85] mt-3 whitespace-pre-line`} data-testid="banner-preview-title">{f.title}</p>
      <p className="font-serif italic text-sm text-cream/85 mt-2 line-clamp-2">{f.text}</p>
    </div>
  </div>
);

export default function AdminBanner() {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    let alive = true;
    api.get("/banner").then(({ data }) => alive && setF(data)).catch(() => toast.error("Could not load the banner"));
    return () => {
      alive = false;
    };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Add a banner photo first");
    setSaving(true);
    try {
      await api.put("/admin/banner", f);
      qc.invalidateQueries({ queryKey: ["/banner"] });
      toast.success("Banner updated on the home page");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  if (!f) return <div className="min-h-screen grid place-items-center kicker">Loading…</div>;

  return (
    <main className="pt-28 pb-24 max-w-[1200px] mx-auto px-5 sm:px-10" data-testid="admin-banner-page">
      <Link to="/admin" className="inline-flex items-center gap-2 kicker hover:text-rose" data-testid="banner-back-link"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      <h1 className="display text-6xl sm:text-7xl mt-4 border-b-2 border-ink pb-6">Home banner</h1>
      <form onSubmit={save} className="grid lg:grid-cols-12 gap-10 mt-10">
        <div className="lg:col-span-6 space-y-6">
          <div>
            <p className="kicker">Photo</p>
            <p className="text-xs text-ink/60 mt-1">Wide, horizontal photos work best (at least 2000 px wide).</p>
            <div className="mt-3">
              {f.image ? (
                <Thumb src={f.image} testId="banner-image" onRemove={() => setF((s) => ({ ...s, image: "" }))} />
              ) : (
                <ImageDrop label="Upload banner photo" testId="banner-image-upload" onUploaded={(url) => setF((s) => ({ ...s, image: url }))} />
              )}
            </div>
          </div>
          <Field label="Small label (pink)"><input value={f.kicker} onChange={set("kicker")} maxLength={80} className="admin-input" data-testid="banner-kicker" /></Field>
          <Field label="Big title" hint="Each new line becomes a line of the headline">
            <textarea required rows={3} value={f.title} onChange={set("title")} maxLength={120} className="admin-input display !text-3xl leading-tight" data-testid="banner-title" />
          </Field>
          <SizePicker value={f.title_size ?? "medium"} onChange={(v) => setF((s) => ({ ...s, title_size: v }))} />
          <Field label="Text"><textarea rows={3} value={f.text} onChange={set("text")} maxLength={400} className="admin-input font-serif italic text-lg" data-testid="banner-text" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Second button label"><input value={f.button_label} onChange={set("button_label")} maxLength={40} className="admin-input" data-testid="banner-button-label" /></Field>
            <Field label="Second button link" hint="e.g. /category/art or https://…"><input value={f.button_link} onChange={set("button_link")} maxLength={300} className="admin-input" data-testid="banner-button-link" /></Field>
          </div>
          <p className="text-sm text-ink/70">The first button always opens the latest cover story.</p>
          <button disabled={saving} className="w-full bg-hot text-ink border-2 border-ink kicker py-4 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="banner-save-btn">
            {saving ? "Saving…" : "Save banner"}
          </button>
        </div>
        <div className="lg:col-span-6">
          <p className="kicker mb-3">Live preview</p>
          <div className="lg:sticky lg:top-28"><MiniPreview f={f} /></div>
        </div>
      </form>
    </main>
  );
}
